import { queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  humanizeStatus,
  TASK_STATUS_TONE,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { LinkifiedText } from './linkified-text.js'
import { ExpandToggle, TaskNotesPreview } from './notes-preview.js'
import {
  CompleteTaskDialog,
  DueDate,
  isScheduled,
  NewTaskDialog,
  ReviewActions,
  StartDate,
  type TaskRow,
  useTaskToggle,
} from './task-shared.js'

/**
 * Tasks linked to one customer or one vendor (t1125), shown on that record's
 * page. Pass `customer` or `vendorId`; "Add" opens the new-task dialog with
 * that link already filled in.
 */
export function LinkedTasksCard({
  customer,
  vendorId,
}: {
  customer?: { id: string; name: string }
  vendorId?: string
}) {
  const { can } = useAuth()
  const canRead = can('tasks:read')
  const canWrite = can('tasks:write')
  const canReview = can('tasks:review')
  const [adding, setAdding] = useState(false)
  const [showScheduled, setShowScheduled] = useState(false)
  const [completing, setCompleting] = useState<TaskRow | null>(null)
  const toggle = useTaskToggle()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const filters = {
    customerId: customer?.id,
    vendorId,
    status: 'all',
    scheduled: 'include',
    pageSize: 50,
  }
  const { data } = useQuery({
    queryKey: queryKeys.tasks.list(filters),
    queryFn: () => api.get<{ items: TaskRow[] }>('/tasks', { query: filters }),
    enabled: canRead,
    refetchInterval: 15_000,
  })

  if (!canRead) return null
  const all = data?.items ?? []
  // Tasks whose start date is still ahead stay out of sight until they start (t1348).
  const scheduledCount = all.filter(isScheduled).length
  const items = showScheduled ? all : all.filter((t) => !isScheduled(t))

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Tasks</CardTitle>
        {canWrite ? (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus /> Add
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {items.length === 0 && scheduledCount === 0 ? (
          <p className="text-sm text-foreground-muted">
            No tasks yet. Add a reminder to call, follow up or check in.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((task) => (
              <li key={task.id} className="text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2">
                    <span className="mt-0.5">
                      <ExpandToggle
                        expanded={expanded.has(task.id)}
                        onToggle={() => toggleExpanded(task.id)}
                        label={task.taskNumber}
                      />
                    </span>
                    <p
                      className={task.status === 'done' ? 'text-foreground-muted line-through' : ''}
                    >
                      {task.title}
                    </p>
                  </div>
                  <Badge tone={TASK_STATUS_TONE[task.status] ?? 'neutral'}>
                    {humanizeStatus(task.status)}
                  </Badge>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-foreground-muted">
                  <span className="font-identifier">{task.taskNumber}</span>
                  <span>{task.assignedStaffId ? task.assignedStaffName : 'Everyone'}</span>
                  <StartDate task={task} />
                  <DueDate task={task} />
                  {canWrite && task.status === 'pending_review' && canReview ? (
                    <ReviewActions task={task} variant="link" />
                  ) : null}
                  {canWrite && !(task.status === 'pending_review' && canReview) ? (
                    <button
                      type="button"
                      className="text-primary hover:underline disabled:opacity-50"
                      disabled={toggle.isPending}
                      onClick={() =>
                        task.status === 'open'
                          ? setCompleting(task)
                          : toggle.mutate({ id: task.id, action: 'reopen' })
                      }
                    >
                      {task.status === 'open' ? 'Mark done' : 'Reopen'}
                    </button>
                  ) : null}
                </div>
                {task.status !== 'open' && task.completionNote ? (
                  <p className="mt-1 whitespace-pre-wrap text-xs text-foreground-muted">
                    “<LinkifiedText text={task.completionNote} />”
                  </p>
                ) : null}
                {expanded.has(task.id) ? (
                  <div className="mt-2 rounded-md bg-surface-sunken/40 p-3">
                    <TaskNotesPreview taskId={task.id} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {scheduledCount > 0 ? (
          <button
            type="button"
            className="mt-3 text-xs text-primary hover:underline"
            onClick={() => setShowScheduled((v) => !v)}
          >
            {showScheduled ? 'Hide scheduled tasks' : `${scheduledCount} scheduled for later`}
          </button>
        ) : null}
      </CardContent>
      <CompleteTaskDialog task={completing} onClose={() => setCompleting(null)} />
      <NewTaskDialog
        open={adding}
        onOpenChange={setAdding}
        customer={customer}
        vendorId={vendorId}
      />
    </Card>
  )
}
